/**
 * @module  public/js/services/authservice.js
 * @class   AuthService
 * @summary Firebase-backed service for sign-in, sign-out, and **auth-ready gating**.
 *
 * @description
 * Wraps Firebase Auth in a promise-aware layer so controllers can *await* a
 * fully-rehydrated session before accessing `currentUser`.
 *
 * ## Key Features
 * – One-time **`#ready`** Promise resolves on the **first** auth state tick
 * – New **`waitForLogin(timeout)`** helper ⇒ guards Focus-Mode & other pages
 * – Simple API for UID lookup, sign-in/out, and auth-state listeners
 * – Graceful errors when the user is unauthenticated
 *
 * ## Public API
 * – `signIn({ email, password })` — authenticate with Firebase credentials
 * – `linkGoogleSignIn()` — link Google Sign-In to the current account
 * – `linkMicrosoftSignIn()` — link Microsoft Sign-In to the current account
 * – `linkEnterpriseSso({ providerId })` — link SAML/OIDC SSO to the current account
 * – `unlinkSignInProvider({ providerId })` — remove a linked sign-in provider
 * – `changePassword({ currentPassword, newPassword })` — update the current email/password credential
 * – `signOut()` — terminate the current session
 * – `waitForLogin([timeout])` — await auth rehydration (⇢ rejects on timeout)
 * – `requireAuth()` — await readiness **and** return user or throw
 * – `currentUserId()` — get UID or `null`
 * – `onAuthStateChanged(cb)` — subscribe to auth changes (unsubscribe fn)
 *
 * ## Dependencies
 * { firebaseApp, getAuth, onAuthStateChanged, signInWithEmailAndPassword, fbSignOut }
 *
 * @author   Auto-Generated
 * @version  1.1.0
 * @since    2025-05-05
 */

import {
  EmailAuthProvider,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  onIdTokenChanged,
  OAuthProvider,
  reauthenticateWithCredential,
  SAMLAuthProvider,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  unlink,
  updatePassword,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';
import {
  firebaseApp,
  authReady,
  getAppCheckToken,
} from '../core/firebase-service.js';
import endpoints from '../core/endpoints-service.js';
import {
  clearPendingLoginIssue,
  persistPendingLoginIssue,
} from '../core/auth/pendingLoginIssue.js';
import {
  DEFAULT_MISSING_TENANT_MESSAGE,
  requestTenantClaimRepair,
} from '../core/auth/tenantClaimRepair.js';

// Idle timeout duration for automatic sign-out (milliseconds).
const ONE_HOUR_IN_MS = 60 * 60 * 1000;

/**
 * Resolve the idle-timeout duration, allowing runtime overrides for testing.
 *
 * Consumers can set `globalThis.TOPSAIL_IDLE_TIMEOUT_IN_MS` to a finite,
 * non-negative number to customise how long inactivity is tolerated before the
 * automatic sign-out triggers.
 *
 * @returns {number} timeout in milliseconds
 */
function resolveIdleTimeoutMs() {
  const scope =
    typeof globalThis === 'object' && globalThis ? globalThis : undefined;
  if (!scope) return ONE_HOUR_IN_MS;

  const override = scope.TOPSAIL_IDLE_TIMEOUT_IN_MS;
  const numericOverride = Number(override);

  if (
    typeof override !== 'undefined' &&
    Number.isFinite(numericOverride) &&
    numericOverride >= 0
  ) {
    return numericOverride;
  }

  return ONE_HOUR_IN_MS;
}

/**
 * Firebase-backed AuthService – central gate for all auth operations.
 */
export default class AuthService {
  /** @type {import('firebase/auth').Auth}    */ #auth = getAuth(firebaseApp);
  /** @type {import('firebase/auth').User?}   */ #currentUser = null;
  /** @type {Set<(u:import('firebase/auth').User|null)=>void>} */ #listeners =
    new Set();
  /** @type {Promise<import('firebase/auth').User|null>}  */ #ready;
  /** @type {number|ReturnType<typeof setTimeout>|null} */ #idleTimeoutId =
    null;
  /** @type {boolean} */ #isIdleTracking = false;
  /** @type {boolean} */ #redirectInFlight = false;
  /** @type {Set<string>} */ #tenantRepairAttemptedUids = new Set();
  /** @type {Map<string, Promise<string|null>>} */ #tenantRepairPromises =
    new Map();
  /** @type {Map<string, Promise<void>>} */ #claimLoadPromises = new Map();
  /**
   * @type {readonly {
   *   target: EventTarget,
   *   type: string,
   *   handler: EventListenerOrEventListenerObject,
   *   options?: boolean | AddEventListenerOptions
   * }[]}
   */
  #idleEvents;

  /**
   * Reset idle timer whenever user interaction is detected.
   * @returns {void}
   */
  #handleUserActivity = () => {
    if (!this.#currentUser) return;
    this.#resetIdleTimer();
  };

  /**
   * Handle document visibility changes as user activity when tab becomes active.
   * @returns {void}
   */
  #handleVisibilityChange = () => {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      this.#handleUserActivity();
    }
  };

  /**
   * Force sign-out when the idle timeout elapses.
   * @returns {Promise<void>}
   */
  #handleIdleTimeout = async () => {
    console.warn('[AuthService] idle timeout reached – signing out user');
    try {
      await fbSignOut(this.#auth);
    } catch (error) {
      console.error('[AuthService] automatic idle sign-out failed', error);
    } finally {
      this.#redirectToLogin('timeout');
    }
  };

  /**
   * Redirect to the login page when running in a browser context.
   * @param {string} [reason='unauthenticated']
   * @returns {void}
   */
  #redirectToLogin(reason = 'unauthenticated') {
    if (this.#redirectInFlight) return;
    if (
      typeof window === 'undefined' ||
      typeof window.location === 'undefined'
    ) {
      return;
    }

    const { location } = window;
    const path = typeof location.pathname === 'string' ? location.pathname : '';
    if (path === '/' || path.endsWith('/index.html')) {
      return;
    }

    this.#redirectInFlight = true;
    if (typeof console !== 'undefined' && typeof console.info === 'function') {
      console.info(`[AuthService] redirecting to login (${reason})`);
    }

    const target = '/index.html';
    if (typeof location.replace === 'function') {
      location.replace(target);
    } else {
      location.href = target;
    }
  }

  /**
   * Persist a one-shot login issue so the login page can render it clearly.
   *
   * @param {string} message - Human-friendly login issue.
   * @returns {void}
   */
  #rememberPendingLoginIssue(message) {
    persistPendingLoginIssue(message);
  }

  /**
   * Ask the backend to repair a missing tenant claim for the signed-in user.
   *
   * The repair runs at most once per uid for the lifetime of this page to
   * avoid loops across auth state and token change listeners.
   *
   * @param {import('firebase/auth').User} user - Signed-in Firebase user.
   * @returns {Promise<string|null>} Tenant id resolved by the backend or null.
   */
  async #repairMissingTenantClaim(user) {
    const uid = String(user?.uid || '').trim();
    if (!uid) {
      return null;
    }
    if (this.#tenantRepairPromises.has(uid)) {
      return this.#tenantRepairPromises.get(uid);
    }
    if (this.#tenantRepairAttemptedUids.has(uid)) {
      return null;
    }

    this.#tenantRepairAttemptedUids.add(uid);
    const repairPromise = (async () => {
      const { tenantId, message } = await requestTenantClaimRepair({
        user,
        endpointUrl: endpoints.setTenantClaim(),
        getAppCheckToken,
        fetchImpl: typeof fetch === 'function' ? fetch : undefined,
      });

      if (message) {
        this.#rememberPendingLoginIssue(message);
        return null;
      }

      clearPendingLoginIssue();
      return tenantId;
    })();

    this.#tenantRepairPromises.set(uid, repairPromise);
    try {
      return await repairPromise;
    } finally {
      this.#tenantRepairPromises.delete(uid);
    }
  }

  constructor() {
    const defaultTarget =
      typeof window !== 'undefined'
        ? window
        : /** @type {EventTarget} */ (null);
    const visibilityTarget =
      typeof document !== 'undefined'
        ? document
        : /** @type {EventTarget} */ (null);

    /** @type {typeof this.#idleEvents} */
    const idleEvents = [];
    if (defaultTarget) {
      idleEvents.push(
        {
          target: defaultTarget,
          type: 'mousemove',
          handler: this.#handleUserActivity,
          options: { passive: true, capture: true },
        },
        {
          target: defaultTarget,
          type: 'keydown',
          handler: this.#handleUserActivity,
          options: { passive: true, capture: true },
        },
        {
          target: defaultTarget,
          type: 'click',
          handler: this.#handleUserActivity,
          options: { passive: true, capture: true },
        },
        {
          target: defaultTarget,
          type: 'touchstart',
          handler: this.#handleUserActivity,
          options: { passive: true, capture: true },
        }
      );
    }

    if (visibilityTarget) {
      idleEvents.push({
        target: visibilityTarget,
        type: 'visibilitychange',
        handler: this.#handleVisibilityChange,
        options: true,
      });
    }

    this.#idleEvents = idleEvents;

    // Resolve #ready exactly once – first auth tick (user or null).
    this.#ready = new Promise((resolve) => {
      onAuthStateChanged(this.#auth, async (u) => {
        this.#currentUser = u;
        this.#syncIdleTracking();
        if (u) await this.#loadClaims(u);
        this.#notify();
        resolve(u);
      });
    });

    // Keep claims fresh on token refreshes
    onIdTokenChanged(this.#auth, async (u) => {
      this.#currentUser = u;
      this.#syncIdleTracking();
      if (u) await this.#loadClaims(u);
      this.#notify();
    });
  }

  /*────────────────── Sign-in / Sign-out ──────────────────*/

  /**
   * Sign in with email/password.
   * @param {{ email:string, password:string }} param0
   */
  async signIn({ email, password }) {
    try {
      await authReady;
    } catch (readyError) {
      console.warn('[AuthService] authReady failure (continuing)', readyError);
    }

    await signInWithEmailAndPassword(this.#auth, email, password);
    // #currentUser updates via listener
  }

  /**
   * Complete a pending Firebase redirect result after returning from an IdP.
   *
   * @returns {Promise<import('firebase/auth').UserCredential|null>} Redirect result or null.
   */
  async completePendingSignInRedirect() {
    try {
      await authReady;
    } catch (readyError) {
      console.warn('[AuthService] authReady failure (continuing)', readyError);
    }

    const result = await getRedirectResult(this.#auth);
    if (result?.user) {
      this.#currentUser = result.user;
    }
    return result;
  }

  /**
   * List the providers currently linked to the signed-in Firebase account.
   *
   * @returns {Array<{ providerId: string, displayName: string, email: string }>} Linked provider descriptors.
   */
  getLinkedSignInMethods() {
    const user = this.#currentUser || this.#auth.currentUser || null;
    if (!user) {
      return [];
    }

    return (Array.isArray(user.providerData) ? user.providerData : [])
      .map((provider) => ({
        providerId: String(provider?.providerId || '').trim(),
        displayName: String(provider?.displayName || '').trim(),
        email: String(provider?.email || '').trim(),
      }))
      .filter((provider) => provider.providerId);
  }

  /**
   * Link Google Sign-In to the currently signed-in Firebase account.
   *
   * @returns {Promise<import('firebase/auth').UserCredential|null>} Google link credential, or null when redirect fallback starts.
   */
  async linkGoogleSignIn() {
    let user = this.#currentUser || this.#auth.currentUser || null;
    if (!user) {
      user = await this.requireAuth();
    }
    const provider = new GoogleAuthProvider();
    provider.addScope?.('profile');
    provider.addScope?.('email');
    provider.setCustomParameters?.({ prompt: 'select_account' });
    let result;
    try {
      result = await linkWithPopup(user, provider);
    } catch (error) {
      if (error?.code === 'auth/popup-blocked') {
        await linkWithRedirect(user, provider);
        return null;
      }
      throw error;
    }
    if (result?.user) {
      this.#currentUser = result.user;
    }
    return result;
  }

  /**
   * Link Microsoft Sign-In to the currently signed-in Firebase account.
   *
   * @returns {Promise<import('firebase/auth').UserCredential|null>} Microsoft link credential, or null when redirect fallback starts.
   */
  async linkMicrosoftSignIn() {
    let user = this.#currentUser || this.#auth.currentUser || null;
    if (!user) {
      user = await this.requireAuth();
    }
    const provider = new OAuthProvider('microsoft.com');
    provider.addScope?.('email');
    provider.addScope?.('profile');
    provider.setCustomParameters?.({ prompt: 'select_account' });
    let result;
    try {
      result = await linkWithPopup(user, provider);
    } catch (error) {
      if (error?.code === 'auth/popup-blocked') {
        await linkWithRedirect(user, provider);
        return null;
      }
      throw error;
    }
    if (result?.user) {
      this.#currentUser = result.user;
    }
    return result;
  }

  /**
   * Link a configured SAML or OIDC provider to the current Firebase account.
   *
   * @param {{ providerId: string, type?: 'saml'|'oidc', loginHint?: string }} params Provider options.
   * @returns {Promise<void>} Resolves after Firebase redirects to the IdP.
   */
  async linkEnterpriseSso({ providerId, type, loginHint } = {}) {
    const user = await this.requireAuth();
    const normalizedProviderId = String(providerId || '').trim();
    if (!normalizedProviderId) {
      throw new Error('Choose a company SSO provider first.');
    }

    let provider;
    const normalizedType = String(type || '')
      .trim()
      .toLowerCase();
    if (normalizedType === 'saml' || normalizedProviderId.startsWith('saml.')) {
      provider = new SAMLAuthProvider(normalizedProviderId);
    } else if (
      normalizedType === 'oidc' ||
      normalizedProviderId.startsWith('oidc.')
    ) {
      provider = new OAuthProvider(normalizedProviderId);
    } else {
      throw new Error('Company SSO provider must be SAML or OIDC.');
    }

    const normalizedLoginHint = String(loginHint || user.email || '').trim();
    if (
      normalizedLoginHint &&
      typeof provider.setCustomParameters === 'function'
    ) {
      provider.setCustomParameters({ login_hint: normalizedLoginHint });
    }

    await linkWithRedirect(user, provider);
  }

  /**
   * Unlink one sign-in provider from the current Firebase account.
   *
   * @param {{ providerId: string }} params Provider to unlink.
   * @returns {Promise<import('firebase/auth').User>} Updated Firebase user.
   */
  async unlinkSignInProvider({ providerId } = {}) {
    const user = await this.requireAuth();
    const normalizedProviderId = String(providerId || '').trim();
    if (!normalizedProviderId) {
      throw new Error('Choose a sign-in method to disconnect.');
    }

    const linkedMethods = this.getLinkedSignInMethods();
    if (linkedMethods.length <= 1) {
      throw new Error(
        'Connect another sign-in method before disconnecting this one.'
      );
    }

    return unlink(user, normalizedProviderId);
  }

  /**
   * Reauthenticate the current user with their email/password credential and
   * replace the password on their Firebase Auth account.
   *
   * @param {{ currentPassword: string, newPassword: string }} params Password change values.
   * @returns {Promise<void>} Resolves after Firebase accepts the new password.
   */
  async changePassword({ currentPassword, newPassword } = {}) {
    const user = await this.requireAuth();
    const email = String(user?.email || '').trim();
    const current = String(currentPassword || '');
    const next = String(newPassword || '');

    if (!email) {
      throw new Error('Email/password sign-in is not available for this user.');
    }
    if (!current) {
      throw new Error('Enter your current password.');
    }
    if (!next || next.length < 6) {
      throw new Error('Use at least 6 characters for your new password.');
    }

    const credential = EmailAuthProvider.credential(email, current);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, next);
  }

  /** Sign out the active user. */
  async signOut() {
    await fbSignOut(this.#auth);
    // #currentUser cleared via listener
  }

  /*──────────────────── Public helpers ────────────────────*/

  /**
   * Wait until Firebase Auth has restored the session *and* a user is present.
   * Rejects if still unauthenticated after the timeout.
   *
   * @param {number} [timeout=5000] – milliseconds before rejection
   * @returns {Promise<import('firebase/auth').User>}
   * @throws  {Error} if timeout elapses or user is not signed-in
   */
  async waitForLogin(timeout = 5000) {
    const user = await Promise.race([
      this.#ready, // resolves once
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error('AuthService: auth timeout')),
          timeout
        )
      ),
    ]);

    if (user) return user;
    throw new Error('AuthService: user not authenticated');
  }

  /**
   * Ensure a logged-in user; convenience wrapper used by legacy code.
   * @returns {Promise<import('firebase/auth').User>}
   */
  async requireAuth() {
    try {
      return await this.waitForLogin();
    } catch (error) {
      this.#redirectToLogin('require-auth');
      throw error;
    }
  }

  /**
   * Current authenticated user, or `null` when signed out.
   * @returns {import('firebase/auth').User|null}
   */
  get currentUser() {
    return this.#currentUser;
  }

  /** UID helper. */
  currentUserId() {
    return this.#currentUser?.uid ?? null;
  }

  /**
   * Subscribe to auth-state changes.
   * @param {(u:import('firebase/auth').User|null)=>void} cb
   * @returns {() => void} unsubscribe
   */
  onAuthStateChanged(cb) {
    this.#listeners.add(cb);
    cb(this.#currentUser); // fire immediately
    return () => this.#listeners.delete(cb);
  }

  /**
   * Subscribe to ID-token changes.
   * @param {(u:import('firebase/auth').User|null)=>void} cb
   * @returns {() => void} unsubscribe
   */
  onIdTokenChanged(cb) {
    return onIdTokenChanged(this.#auth, cb);
  }

  /*────────────────────── internals ──────────────────────*/
  async #loadClaims(user) {
    const uid = String(user?.uid || '').trim();
    if (!uid) {
      return;
    }

    if (this.#claimLoadPromises.has(uid)) {
      await this.#claimLoadPromises.get(uid);
      return;
    }

    let loadPromise;
    loadPromise = (async () => {
      try {
        let tokenResult;
        try {
          tokenResult = await user.getIdTokenResult();
        } catch (tokenError) {
          const error = /** @type {{ code?: string }} */ (tokenError) ?? {};
          const code = typeof error.code === 'string' ? error.code : '';

          if (code !== 'auth/internal-error') {
            throw tokenError;
          }

          await new Promise((resolve) => setTimeout(resolve, 250));
          tokenResult = await user.getIdTokenResult(true);
        }

        let { claims } = tokenResult;
        if (!claims?.tenantId) {
          const repairedTenantId = await this.#repairMissingTenantClaim(user);
          if (repairedTenantId) {
            ({ claims } = await user.getIdTokenResult(true));
          }
        }

        if (claims?.tenantId) {
          user.tenantId = claims.tenantId;
          clearPendingLoginIssue();
          return;
        }

        this.#rememberPendingLoginIssue(DEFAULT_MISSING_TENANT_MESSAGE);
        this.#redirectToLogin('missing-tenant');
      } catch (err) {
        const error = /** @type {{ code?: string }} */ (err) ?? {};
        const code = typeof error.code === 'string' ? error.code : '';

        if (
          code === 'auth/id-token-revoked' ||
          code === 'auth/user-token-expired'
        ) {
          console.warn(
            '[AuthService] session expired or revoked – signing out'
          );
          try {
            await fbSignOut(this.#auth);
          } catch (signOutError) {
            console.error(
              '[AuthService] automatic sign-out failed',
              signOutError
            );
          }
          this.#redirectToLogin('token-expired');
          return;
        }

        if (code === 'auth/internal-error') {
          return;
        }

        console.warn('[AuthService] failed to load claims', err);
      } finally {
        if (this.#claimLoadPromises.get(uid) === loadPromise) {
          this.#claimLoadPromises.delete(uid);
        }
      }
    })();

    this.#claimLoadPromises.set(uid, loadPromise);
    await loadPromise;
  }
  /**
   * Ensure idle tracking is active only when a user is signed in.
   * @returns {void}
   */
  #syncIdleTracking() {
    if (this.#currentUser) {
      this.#startIdleTracking();
    } else {
      this.#stopIdleTracking();
    }
  }

  /**
   * Attach listeners and schedule the idle timeout when authenticated.
   * @returns {void}
   */
  #startIdleTracking() {
    if (this.#isIdleTracking || this.#idleEvents.length === 0) return;

    for (const { target, type, handler, options } of this.#idleEvents) {
      target.addEventListener(type, handler, options);
    }

    this.#isIdleTracking = true;
    this.#resetIdleTimer();
  }

  /**
   * Remove listeners and cancel the idle timeout when signed out.
   * @returns {void}
   */
  #stopIdleTracking() {
    if (!this.#isIdleTracking) return;

    for (const { target, type, handler, options } of this.#idleEvents) {
      const removeOptions =
        typeof options === 'boolean'
          ? options
          : options && 'capture' in options
            ? (options.capture ?? false)
            : false;
      target.removeEventListener(type, handler, removeOptions);
    }

    this.#isIdleTracking = false;
    if (this.#idleTimeoutId !== null) {
      clearTimeout(this.#idleTimeoutId);
      this.#idleTimeoutId = null;
    }
  }

  /**
   * (Re)schedule the idle timeout countdown.
   * @returns {void}
   */
  #resetIdleTimer() {
    if (this.#idleTimeoutId !== null) {
      clearTimeout(this.#idleTimeoutId);
    }

    this.#idleTimeoutId = setTimeout(
      this.#handleIdleTimeout,
      resolveIdleTimeoutMs()
    );
  }
  #notify() {
    for (const fn of this.#listeners) {
      try {
        fn(this.#currentUser);
      } catch {
        /* no-op */
      }
    }

    if (!this.#currentUser) {
      this.#redirectToLogin('unauthenticated');
    }
  }
}
